import { ChevronRight, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Track {
  track_name: string;
  album_name: string;
  popularity: number;
  preview_url: string | null;
  external_url: string;
  album_image: string;
}

const AWSLambdaEndpoint = "https://wbpvo3cqbuk74oz2cghp4lgzgu0aisld.lambda-url.eu-west-2.on.aws/"

interface ApiResponse {
  topTracks: Track[];
}

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

export default function SpotifyReleases() {
  const [tracks, setTracks] = useState<Track[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const fetchTracks = async () => {
      try {
        const response = await fetch(AWSLambdaEndpoint);
        if (!response.ok) {
          throw new Error('Failed to fetch tracks');
        }
        const data: ApiResponse = await response.json();
        setTracks(data.topTracks);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchTracks();
  }, []);

  if (loading) return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="px-4 py-6"
    >
      Releases laden...
    </motion.div>
  );
  
  if (error) return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="px-4 py-6 text-red-500"
    >
      Error: {error}
    </motion.div>
  );

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="px-4 py-6"
    >
      <motion.div 
        layout
        className="flex justify-between items-center mb-3"
      >
        <motion.h2 
          layout
          className="text-lg font-semibold"
        >
          Releases
        </motion.h2>
        <motion.button 
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-sm text-gray-400 hover:text-white transition-all duration-200 flex items-center px-3 py-1.5 rounded-md hover:bg-zinc-800/50"
        >
          {isExpanded ? (
            <>
              Sluiten
              <X size={16} />
            </>
          ) : (
            `Toon alles (${tracks.length})`
          )}
        </motion.button>
      </motion.div>
      
      <motion.div 
        layout
        variants={container}
        initial="hidden"
        animate="show"
        className={`
          ${isExpanded 
            ? 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4' 
            : 'flex gap-4 overflow-x-auto pb-4 snap-x'
          }
        `}
      >
        {tracks.map((track, index) => (
          <motion.a
            key={track.external_url}
            href={track.external_url}
            target="_blank"
            rel="noopener noreferrer" 
            variants={item}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`
              ${isExpanded ? 'w-full' : 'min-w-[150px]'}
              bg-zinc-900 rounded-lg overflow-hidden snap-start p-1 my-2
            `}
          >
            <motion.div 
              layout
              className="aspect-square bg-cover bg-center rounded-lg" 
              style={{ backgroundImage: `url(${track.album_image})` }}
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            />
            <motion.div 
              layout
              className="p-3"
            >
              <motion.h3 
                layout
                className="font-medium"
              >
                {track.track_name}
              </motion.h3>
              <motion.p 
                layout
                className="text-sm text-gray-400"
              >
                {track.album_name}
              </motion.p>
            </motion.div>
          </motion.a>
        ))}
      </motion.div>
    </motion.div>
  );
}
